import React, { useState, useRef, useEffect } from 'react';
import { useSettings } from '../contexts/SettingsContext';

/**
 * MultiSelectDropdown - A dropdown that allows multiple selections
 * @param {string} label - Label for the field
 * @param {string} placeholder - Placeholder text when no items selected
 * @param {Array<{value: string, label: string}>} options - Array of options with value and label
 * @param {Array<string>} selectedValues - Array of currently selected values
 * @param {function} onChange - Callback when selection changes, receives array of selected values
 * @param {string} error - Error message to display
 * @param {boolean} disabled - Whether the component is disabled
 * @param {boolean} dropUpRight - Whether to drop up and align right
 */
const MultiSelectDropdown = ({
  label,
  placeholder = 'Select options...',
  options = [],
  selectedValues = [],
  onChange,
  error,
  disabled = false,
  dropUpRight = false,
  className = '',
}) => {
  const { settings } = useSettings();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#20C997';
  
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleOptionToggle = (value) => {
    if (disabled) return;
    
    const newSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    
    onChange(newSelectedValues);
  };

  const handleSelectAll = () => {
    if (disabled) return;
    if (selectedValues.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map(opt => opt.value));
    }
  };

  // Get display text for selected values
  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholder;
    const selectedLabels = options
      .filter(opt => selectedValues.includes(opt.value))
      .map(opt => opt.label);
    return selectedLabels.join(', ');
  };

  return (
    <div className={`flex flex-col relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors duration-250">
          {label}
        </label>
      )}
      
      {/* Dropdown Trigger */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`relative w-full h-12 px-4 pr-10 text-left rounded-xl border flex items-center justify-between transition-all duration-200 ${
          error ? 'border-red-500' : 'border-gray-200 dark:border-gray-600'
        } ${
          disabled 
            ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed opacity-60' 
            : 'bg-gray-50 dark:bg-gray-800 cursor-pointer hover:border-gray-300 dark:hover:border-gray-500'
        }`}
        style={
          isOpen && !error
            ? {
                border: '1px solid transparent',
                backgroundImage: `linear-gradient(#fff, #fff), linear-gradient(135deg, ${primaryColor} 25%, ${secondaryColor} 75%)`,
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
              }
            : {}
        }
      >
        <span 
          className={`truncate ${
            selectedValues.length === 0 
              ? 'text-gray-400' 
              : 'text-gray-800 dark:text-gray-100'
          }`}
          style={{display:'block', width:'calc(100% - 1.5rem)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}
        >
          {getDisplayText()}
        </span>
        {/* Arrow - matching DropdownBox style */}
        <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
          <span
            style={{
              display: 'inline-block',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: `6px solid ${isOpen ? primaryColor : '#9CA3AF'}`,
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />
        </span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="relative">
          <ul
            className={
              `absolute z-20 w-full max-h-60 overflow-auto rounded-xl border bg-white dark:bg-gray-800 shadow-lg ` +
              (dropUpRight
                ? ' right-0 bottom-full origin-bottom-right'
                : ' mt-2 left-0 origin-top-left')
            }
            style={dropUpRight ? {marginBottom: '3.5rem'} : {}}
          >
            {/* Select All Option */}
            <li
              onClick={handleSelectAll}
              className="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-600 flex items-center gap-2 transition-colors"
            >
              <div
                className={`w-4 h-4 rounded border flex items-center justify-center transition-colors duration-200 ${
                  selectedValues.length === options.length
                    ? 'bg-gradient-to-b from-[#3B5BDB] to-[#20C997] border-none'
                    : selectedValues.length > 0
                      ? 'bg-blue-300 border-none'
                    : 'border-gray-300 dark:border-gray-600'
              }`}
              style={{ minWidth: 16, minHeight: 16 }}
            >
              {selectedValues.length > 0 && (
                <svg
                  className="w-3 h-3 text-white"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  viewBox="0 0 24 24"
                >
                  {selectedValues.length === options.length ? (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
                  )}
                </svg>
              )}
            </div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {selectedValues.length === options.length ? 'Deselect All' : 'Select All'}
            </span>
            </li>
          
            {/* Options */}
            {options.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <li
                  key={option.value}
                  onClick={() => handleOptionToggle(option.value)}
                  className={`px-4 py-2 cursor-pointer flex items-center gap-2 transition-colors duration-150 ${
                    isSelected
                      ? 'bg-blue-50 dark:bg-blue-900/30'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded border flex items-center justify-center transition-colors duration-200 ${
                      isSelected
                        ? 'bg-gradient-to-b from-[#3B5BDB] to-[#20C997] border-none'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                    style={{ minWidth: 16, minHeight: 16 }}
                  >
                    {isSelected && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className={`text-sm ${
                    isSelected
                      ? 'text-blue-700 dark:text-blue-300 font-medium'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {option.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      
      {error && (
        <span className="mt-1 text-xs text-red-500">{error}</span>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
